import { SearchParametersResponse } from "@fartherfinance/frontend/api/TaskWorkflows/requests/searchParameters";
import { Expr } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import { AdvisorRequestConfig } from "@fartherfinance/frontend/api/Types";

// ===== CORE FORM ENGINE TYPES =====

export type FormData = Record<string, unknown>;

// ===== EXPRESSION TYPES =====

/**
 * External resources that can be passed to expression resolvers
 * This can be extended with additional resources as needed
 */
export type ExternalResources = {
  taskMeta?: {
    isSystemGenerated?: boolean;
  };
  authConfig?: AdvisorRequestConfig | null;
  [key: string]: unknown;
};

type ExpressionResolverConfig = {
  fieldValue?: unknown;
  templateFieldValueResolver: (field: string) => unknown;
  expression: Expr;
  externalResources?: ExternalResources;
};

export type ExpressionResolverFn = (
  config: ExpressionResolverConfig
) => Promise<unknown>;

// ===== RESOURCE TYPES =====

export type ResourceTypeResponse =
  SearchParametersResponse["data"][number]["values"][number];

interface ResourceTypeWithValue {
  type: string;
  value: string | number;
  kind?: string[] | string;
}

export const isResourceTypeWithValue = (
  param: ResourceTypeResponse
): param is ResourceTypeWithValue => {
  return (
    typeof param === "object" &&
    "value" in param &&
    "type" in param &&
    (typeof param.value === "string" || typeof param.value === "number")
  );
};

interface BaseOption {
  label: string;
  value: string;
  serviceTeamType?: string; // Optional field for service team type sorting
}

interface ResolvedResourceType {
  type: string;
  value?: string;
  kind?: string[] | string;
}

export type EnumOption = (ResolvedResourceType & BaseOption) | BaseOption;

/**
 * Resolver function type for resource types
 * Takes an auth/request config and resource values, returns EnumOptions
 */
export type ResourceResolver = (
  requestConfig: AdvisorRequestConfig,
  values: ResourceTypeResponse[]
) => Promise<EnumOption[]>;

// ===== VALIDATION TYPES =====

export type ValidationResult = boolean | string;

// eslint-disable-next-line import/no-unused-modules
export type ValidationRule = string | boolean;
export type ValidateFn = (
  value: unknown
) => boolean | string | Promise<boolean | string>;

export interface ValidationRules {
  required: ValidationRule;
  deps?: string[];
  validate?: ValidateFn;
}

// ===== FIELD OPTION TYPES =====

export type OptionsMap = Record<string, EnumOption[]>;
export type LoadingMap = Record<string, boolean>;

// ===== FORM CONTEXT TYPES =====

export interface InitialFieldDataContextValue {
  optionsMap: OptionsMap;
  loadingMap: LoadingMap;
  allOptionsLoaded: boolean;
  matchInitialValue: (fieldKey: string, value: unknown) => unknown;
  processedInitialValues?: Record<string, unknown>;
}
